import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  CardHeader,
  Button,
  Badge,
  FormGroup,
  Input,
  Form,
  CardFooter,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { Link, useParams } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  useGetPurchaseRequestByIdQuery,
  useCreateSuppliersAutoMapMutation,
  useGetPurchaseOrderByRequestIdQuery,
  useCreatePurchaseAttachmentMutation,
  useDeletePurchaseAttachmentByIdMutation,
  useUpdatePurchaseRequestItemsMutation,
  useUpdatePurchaseRequestMutation,
  useGetPurchaseRequestDocumentsByPurchaseRequestQuery,
  useCancelPurchaseRequestMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { IsValidPermissions } from "Routes/AuthProtected";
import ProductThumbnail from "../CommonComponents/ProductThumbnail";
import CancelReasonModal from "../../../Components/Common/CancelReasonModal";
import { getUserId } from "helpers/jwt-token-access/auth-token-header";
import { toast } from "react-toastify";

interface Attachment {
  id: number;
  file_url: string;
  purchase_request_number: string;
  created_at: string;
  modified_at: string;
  active: boolean;
  name: string;
  description: string;
  file: string;
  created_by: number;
  modified_by: number;
  purchase_request: number;
  file_name?: string;
  file_size?: number;
}

const PurchaseOrderDetail = () => {
  const { purchaseId } = useParams();

  const {
    data: purchaseOrder,
    isLoading,
    error,
    refetch: refetchPurchaseProducts,
  } = useGetPurchaseRequestByIdQuery(purchaseId);

  const { data: purchaseOrdersData, refetch: purchaseOrdersDataRefetch } =
    useGetPurchaseOrderByRequestIdQuery(purchaseId);

  const {
    data: documentChecklistData,
    isLoading: isDocumentChecklistLoading,
    error: documentChecklistError,
  } = useGetPurchaseRequestDocumentsByPurchaseRequestQuery(purchaseId as string, {
    skip: !purchaseId,
  });

  const [createPurchaseAttachment, { isLoading: isCreatingAttachment }] =
    useCreatePurchaseAttachmentMutation();
  const [deletePurchaseAttachmentById, { isLoading: isDeletingAttachment }] =
    useDeletePurchaseAttachmentByIdMutation();

  const [createSuppliersAutoMap] = useCreateSuppliersAutoMapMutation();

  const [updatePurchaseRequestItem, { isLoading: isUpdatingItem }] =
    useUpdatePurchaseRequestItemsMutation();
  const [updatePurchaseRequest, { isLoading: isUpdatingStatus }] =
    useUpdatePurchaseRequestMutation();

  const [cancelPurchaseRequest, { isLoading: isCancellingPR }] =
    useCancelPurchaseRequestMutation();
  const [cancelPRModalOpen, setCancelPRModalOpen] = useState(false);
  const [cancelPRError, setCancelPRError] = useState<string | null>(null);
  const [cancelPRErrorItems, setCancelPRErrorItems] = useState<string[]>([]);

  const poList: any[] = Array.isArray(purchaseOrdersData)
    ? purchaseOrdersData
    : (purchaseOrdersData as any)?.results || [];
  const activePOCount = poList.filter(
    (po: any) => (po?.po_status || "").toLowerCase() !== "cancelled",
  ).length;
  const isPRCancelled =
    (purchaseOrder?.status || "").toLowerCase() === "cancelled";

  const handleCancelPR = async (reason: string) => {
    if (!purchaseId) return;
    setCancelPRError(null);
    setCancelPRErrorItems([]);
    try {
      const res: any = await cancelPurchaseRequest({
        id: Number(purchaseId),
        reason,
        cancelled_by_user_id: getUserId(),
      });
      if (res?.error) {
        const data = res.error?.data || {};
        const details = data.active_purchase_order_details || [];
        setCancelPRError(
          details.length
            ? "Cannot cancel this purchase request. Cancel the following purchase order(s) first:"
            : data.error || "Failed to cancel purchase request.",
        );
        setCancelPRErrorItems(
          details.map(
            (d: any) =>
              `${d.purchase_order_number || "Draft PO"} — ${d.supplier_name || "N/A"} (${d.po_status})`,
          ),
        );
        return;
      }
      toast.success(res?.data?.message || "Purchase request cancelled successfully.");
      setCancelPRModalOpen(false);
      refetchPurchaseProducts();
    } catch (e) {
      setCancelPRError("Failed to cancel purchase request.");
    }
  };

  // --- UI state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [rfqItems, setRfqItems] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("1"); // For tabs

  // Attachment modal states
  const [attachmentModal, setAttachmentModal] = useState(false);
  const [newAttachment, setNewAttachment] = useState({
    name: "",
    description: "",
    file: null as File | null,
  });

  // Action modals & state
  const [implementModal, setImplementModal] = useState(false);
  const [confirmImplementModal, setConfirmImplementModal] = useState(false);
  const [actionResultModal, setActionResultModal] = useState(false);

  // Reject modal state (purchase request rejection)
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [selectedItemForAction, setSelectedItemForAction] = useState<any>(null);
  const [implementForm, setImplementForm] = useState({
    total_no_of_items_implemented: "",
    balance_item_count: "",
  });
  const [implementConfirmationFile, setImplementConfirmationFile] = useState<File | null>(null);
  const [actionResult, setActionResult] = useState<any>(null);

  // Initialize RFQ items when purchaseOrder changes
  useEffect(() => {
    if (purchaseOrder?.items) {
      const rfqItemsFromAPI = purchaseOrder.items.filter(
        (item: any) => item.is_rfq === true
      );
      if (rfqItems.length === 0 || rfqItems.length !== rfqItemsFromAPI.length) {
        const itemsWithSelection = rfqItemsFromAPI.map((item: any) => ({
          ...item,
          selected: false,
        }));
        setRfqItems(itemsWithSelection);
      }
    }
  }, [purchaseOrder?.items]);

  // Attachment helpers
  const toggleAttachmentModal = () => {
    setAttachmentModal(!attachmentModal);
    if (attachmentModal) {
      setNewAttachment({ name: "", description: "", file: null });
    }
  };

  const handleAttachmentInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewAttachment((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setNewAttachment((prev) => ({
      ...prev,
      file,
      name: file?.name || prev.name,
    }));
  };

  const handleCreateAttachment = async () => {
    if (!purchaseId || !newAttachment.file) {
      alert("Please select a file to upload");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", newAttachment.name || newAttachment.file.name);
      formData.append("description", newAttachment.description);
      formData.append("file", newAttachment.file);
      formData.append("purchase_request", purchaseId);

      await createPurchaseAttachment(formData).unwrap();
      refetchPurchaseProducts();
      toggleAttachmentModal();
      alert("Attachment uploaded successfully!");
    } catch (error) {
      console.error("Failed to upload attachment:", error);
      alert("Failed to upload attachment. Please try again.");
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!window.confirm("Are you sure you want to delete this attachment?")) {
      return;
    }
    try {
      // Convert number to string
      await deletePurchaseAttachmentById(attachmentId.toString()).unwrap();
      refetchPurchaseProducts();
      alert("Attachment deleted successfully!");
    } catch (error) {
      console.error("Failed to delete attachment:", error);
      alert("Failed to delete attachment. Please try again.");
    }
  };

  // --- Item status helpers
  const isItemDelivered = (item: any) => {
    return item?.is_po_delivered === true;
  };

  const isItemPendingDelivery = (item: any) => {
    const status = (item?.delivery_status || item?.po_item_status || "").toLowerCase();
    return status === "pending" || status === "partially_delivered" || !isItemDelivered(item);
  };

  // Check if item is delivered (for showing implement button)
  const isItemStatusDelivered = (item: any) => {
    const status = (item?.delivery_status || item?.po_item_status || "").toLowerCase();
    return status === "delivered" || status === "partially_delivered";
  };

  // ONLY show implement button when item is delivered AND not fully implemented
  const canShowImplementButton = (item: any) => {
    const alreadyImplementedOnce =
      (item?.total_item_implemented !== null &&
        item?.total_item_implemented !== undefined) ||
      (item?.balance_item_count !== null &&
        item?.balance_item_count !== undefined);

    return (isItemDelivered(item) || isItemStatusDelivered(item)) &&
      !alreadyImplementedOnce &&
      (parseInt(item?.balance_item_count || item?.quantity) > 0 ||
        parseInt(item?.total_item_implemented || 0) < parseInt(item?.quantity || 0));
  };

  const isFullyImplemented = (item: any) => {
    return item?.balance_item_count === 0 &&
      parseInt(item?.total_item_implemented || 0) >= parseInt(item?.quantity || 0);
  };

  // Implement action flow
  const openImplementModal = (item: any) => {
    setSelectedItemForAction(item);
    setImplementConfirmationFile(null);
    const currentImplemented = parseInt(item?.total_item_implemented || 0);
    const currentBalance = parseInt(item?.balance_item_count || item?.quantity || 0);

    setImplementForm({
      total_no_of_items_implemented: currentImplemented.toString(),
      balance_item_count: currentBalance.toString(),
    });
    setImplementModal(true);
  };

  const closeImplementModal = () => {
    setImplementModal(false);
    setConfirmImplementModal(false);
    setSelectedItemForAction(null);
    setImplementConfirmationFile(null);
  };

  const handleImplementInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    if (selectedItemForAction && name === "total_no_of_items_implemented") {
      const maxQuantity = parseInt(selectedItemForAction.quantity);
      const implemented = parseInt(value) || 0;

      if (implemented > maxQuantity) {
        alert(`Cannot implement more than ${maxQuantity} items (total quantity)`);
        return;
      }

      const balance = maxQuantity - implemented;
      setImplementForm({
        total_no_of_items_implemented: Math.min(implemented, maxQuantity).toString(),
        balance_item_count: Math.max(balance, 0).toString(),
      });
    } else {
      setImplementForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImplementSubmit = async () => {
    if (!selectedItemForAction) return;

    const implemented = parseInt(
      implementForm.total_no_of_items_implemented || "0",
      10
    );
    const balance = parseInt(implementForm.balance_item_count || "0", 10);
    const maxQuantity = parseInt(selectedItemForAction.quantity);

    if (Number.isNaN(implemented) || Number.isNaN(balance)) {
      alert("Please enter valid numeric values.");
      return;
    }

    if (implemented > maxQuantity) {
      alert(`Cannot implement more than ${maxQuantity} items (total quantity)`);
      return;
    }

    if (implemented + balance !== maxQuantity) {
      alert(`Total implemented (${implemented}) + balance (${balance}) must equal total quantity (${maxQuantity})`);
      return;
    }

    const isDelivered = isItemDelivered(selectedItemForAction) || isItemStatusDelivered(selectedItemForAction);
    if (isDelivered && !implementConfirmationFile) {
      alert("Confirmation document or attachment is required when status is Delivered.");
      return;
    }

    setConfirmImplementModal(true);
  };

  const handleImplementConfirmSubmit = async () => {
    if (!selectedItemForAction) return;

    const isDelivered = isItemDelivered(selectedItemForAction) || isItemStatusDelivered(selectedItemForAction);
    if (isDelivered && !implementConfirmationFile) return;

    const implemented = parseInt(
      implementForm.total_no_of_items_implemented || "0",
      10
    );
    const balance = parseInt(implementForm.balance_item_count || "0", 10);

    try {
      const formData = new FormData();
      formData.append("total_item_implemented", String(implemented));
      formData.append("balance_item_count", String(balance));
      if (implementConfirmationFile) {
        formData.append("implementation_confirmation_document", implementConfirmationFile);
      }

      const res = await updatePurchaseRequestItem({
        id: selectedItemForAction.id,
        productData: formData,
      }).unwrap();

      setActionResult(res);
      setConfirmImplementModal(false);
      closeImplementModal();
      refetchPurchaseProducts();
      alert("Implementation updated successfully.");
    } catch (err: any) {
      console.error("Failed to update implemented counts:", err);
      alert(
        err?.data?.error ||
          err?.data?.detail ||
          "Failed to update implementation. Please try again."
      );
    }
  };

  // Purchase request approve / reject (when status is "submitted")
  const canShowApproveReject =
    purchaseOrder?.status?.toLowerCase() === "submitted" &&
    purchaseId &&
    (IsValidPermissions(["EDIT_PURCHASE_REQUEST"]) || IsValidPermissions(["ADD_PURCHASE_REQUEST"]));

  const handleApprove = async () => {
    if (!purchaseId) return;
    try {
      await updatePurchaseRequest({
        id: purchaseId,
        productData: { status: "approved" },
      }).unwrap();
      refetchPurchaseProducts();
      alert("Purchase request approved successfully.");
    } catch (err) {
      console.error("Failed to approve purchase request:", err);
      alert("Failed to approve. Please try again.");
    }
  };

  const handleRejectConfirm = async () => {
    if (!purchaseId) return;
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }
    try {
      await updatePurchaseRequest({
        id: purchaseId,
        productData: {
          status: "rejected",
          rejection_reason: rejectReason.trim(),
        },
      }).unwrap();
      setRejectModal(false);
      setRejectReason("");
      refetchPurchaseProducts();
      alert("Purchase request rejected.");
    } catch (err) {
      console.error("Failed to reject purchase request:", err);
      alert("Failed to reject. Please try again.");
    }
  };

  const toggleRejectModal = () => {
    setRejectModal((prev) => !prev);
    if (!rejectModal) setRejectReason("");
  };

  // Small helpers
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileName = (filePath: string) => {
    return filePath.split("/").pop() || "Unknown file";
  };

  const getStatusBadge = (item: any) => {
    if (isItemDelivered(item)) {
      if (isFullyImplemented(item)) {
        return <Badge color="success">Fully Implemented</Badge>;
      } else if (canShowImplementButton(item)) {
        return <Badge color="warning">Ready for Implementation</Badge>;
      }
      return <Badge color="primary">Delivery Confirmed</Badge>;
    }

    const status = item?.delivery_status || item?.po_item_status;
    switch (status?.toLowerCase()) {
      case "delivered":
        return <Badge color="info">Delivered</Badge>;
      case "partially_delivered":
        return <Badge color="info">Partially Delivered</Badge>;
      case "pending":
        return <Badge color="secondary">Pending Delivery</Badge>;
      default:
        return <Badge color="secondary">Pending</Badge>;
    }
  };

  const hasPurchaseOrderItems =
    purchaseOrder?.items && purchaseOrder.items.length > 0;

  document.title = "Purchase Request Details";

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="alert alert-danger">
            Error loading Purchase Request Details.
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Purchase Request Details" pageTitle="Inventory" />

        <Row>
          <Col xl={12}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      <h5 className="card-title mb-0 me-2">
                        {purchaseOrder?.purchase_request_number}
                      </h5>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="text-muted me-2">
                        Purchase Fullfilment Number #{" "}
                        {purchaseOrder?.purchase_order_number || "N/A"}
                      </span>
                      {purchaseOrder?.status && (
                        <span className="ms-2">
                          <Badge color={isPRCancelled ? "danger" : "secondary"}>
                            {purchaseOrder.status}
                          </Badge>
                        </span>
                      )}
                    </div>
                  </div>
                  {!isPRCancelled &&
                    IsValidPermissions(["DELETE_PURCHASE_REQUEST"]) && (
                      <div
                        className="d-flex gap-2"
                        title={
                          activePOCount > 0
                            ? "Cancel all purchase orders under this request first."
                            : undefined
                        }
                      >
                        <Button
                          color="danger"
                          size="sm"
                          disabled={activePOCount > 0}
                          onClick={() => {
                            setCancelPRError(null);
                            setCancelPRModalOpen(true);
                          }}
                        >
                          <i className="ri-close-circle-line align-middle me-1"></i>
                          Cancel PR
                        </Button>
                      </div>
                    )}
                  {/* {canShowApproveReject && (
                    <div className="d-flex gap-2">
                      <Button
                        color="success"
                        size="sm"
                        onClick={handleApprove}
                        disabled={isUpdatingStatus}
                      >
                        <i className="ri-check-line align-middle me-1"></i>
                        Approve
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={toggleRejectModal}
                        disabled={isUpdatingStatus}
                      >
                        <i className="ri-close-line align-middle me-1"></i>
                        Reject
                      </Button>
                    </div>
                  )} */}
                </div>
              </CardHeader>

              <CardBody>
                {/* Summary top */}
                <Row className="mb-4">
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Title</label>
                      <p className="text-muted mb-0">
                        {purchaseOrder?.title || "N/A"}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Summary</label>
                      <p className="text-muted mb-0">
                        {purchaseOrder?.summary || "No summary provided"}
                      </p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Project</label>
                      <p className="text-muted mb-0">
                        {purchaseOrder?.project_name || "N/A"}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Submitted By
                      </label>
                      <p className="text-muted mb-0">
                        {purchaseOrder?.submitted_by_name}
                      </p>
                    </div>
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Tabs for Products, Documents & Checklist */}
                <Nav tabs className="nav-tabs-custom nav-justified mb-3">
                  <NavItem>
                    <NavLink
                      className={activeTab === "1" ? "active" : ""}
                      onClick={() => setActiveTab("1")}
                    >
                      <i className="ri-shopping-cart-line align-middle me-2"></i>
                      Purchase Request Products
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="ri-attachment-line align-middle me-2"></i>
                      Documents & Attachments
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "3" ? "active" : ""}
                      onClick={() => setActiveTab("3")}
                    >
                      <i className="ri-file-list-3-line align-middle me-2"></i>
                      Document Checklist
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                  {/* Products Tab */}
                  <TabPane tabId="1">
                    <Row>
                      <Col lg={12}>
                        <Card className="border-0 shadow-sm">
                          <CardHeader className="bg-light border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                              <h5 className="card-title mb-0 text-primary">
                                <i className="ri-shopping-cart-line align-middle me-2"></i>
                                Purchase Request Products
                              </h5>
                            </div>
                          </CardHeader>

                          <CardBody className="p-0">
                            {hasPurchaseOrderItems ? (
                              <div className="table-responsive">
                                <div className="table-body-container">
                                  <table className="table table-nowrap align-middle table-borderless mb-0">
                                    <thead
                                      className="table-light text-muted"
                                      style={{
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1,
                                      }}
                                    >
                                      <tr>
                                        <th scope="col" className="ps-4">
                                          Product Details
                                        </th>
                                        <th scope="col" className="text-center">
                                          Quantity
                                        </th>
                                        <th scope="col" className="text-center">
                                          Expected Delivery Date
                                        </th>
                                        <th scope="col" className="text-center">
                                          Status
                                        </th>
                                        <th scope="col" className="text-center">
                                          Implementation Progress
                                        </th>
                                        <th
                                          scope="col"
                                          className="text-center pe-4"
                                        >
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {purchaseOrder?.items?.map(
                                        (item: any, index: number) => (
                                          <React.Fragment key={item.id}>
                                            <tr>
                                              <td className="ps-4">
                                                <div className="d-flex align-items-center">
                                                  <div className="flex-shrink-0 me-3">
                                                    <ProductThumbnail
                                                      attachment_urls={item?.attachment_urls}
                                                      alt={item?.product_name || ''}
                                                      size="xs"
                                                      className="me-2"
                                                    />

                                                  </div>
                                                  <div className="flex-grow-1">
                                                    <h6 className="mb-1 fw-semibold">
                                                      {item.product_name}
                                                    </h6>
                                                    <small className="text-muted d-block">
                                                      Category:{" "}
                                                      {item.category_name}
                                                    </small>
                                                  </div>
                                                </div>
                                              </td>
                                              <td className="text-center">
                                                <span className="fw-bold text-dark">
                                                  {item.quantity}
                                                </span>
                                                {item.measurement_name && (
                                                  <div className="text-muted fs-12">
                                                    {item.measurement_name}
                                                  </div>
                                                )}
                                              </td>
                                              <td className="text-center">
                                                {item.expected_delivery_date ? (
                                                  <Badge
                                                    color="info"
                                                    className="fs-12"
                                                  >
                                                    {new Date(
                                                      item.expected_delivery_date
                                                    ).toLocaleDateString()}
                                                  </Badge>
                                                ) : (
                                                  <span className="text-muted fs-12">
                                                    Not specified
                                                  </span>
                                                )}
                                              </td>
                                              <td className="text-center">
                                                {getStatusBadge(item)}
                                              </td>
                                              <td className="text-center">
                                                {isItemDelivered(item) || isItemStatusDelivered(item) ? (
                                                  <div>
                                                    <div className="fw-semibold">
                                                      Implemented: {item.total_item_implemented || 0} / {item.quantity}
                                                    </div>
                                                    <div className="text-muted fs-12">
                                                      Balance: {item.balance_item_count || item.quantity}
                                                    </div>
                                                    {isFullyImplemented(item) && (
                                                      <Badge color="success" className="mt-1">
                                                        Fully Implemented
                                                      </Badge>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <span className="text-muted">Not delivered yet</span>
                                                )}
                                              </td>
                                              <td className="text-center pe-4">
                                                <div className="d-flex gap-2 justify-content-center">
                                                  {/* Implement Button - Show only when item is delivered and user has permission */}
                                                  {canShowImplementButton(item) && IsValidPermissions(["EDIT_PURCHASE_REQUEST"]) && (
                                                    <Button
                                                      color="primary"
                                                      size="sm"
                                                      onClick={() =>
                                                        openImplementModal(item)
                                                      }
                                                      title="Update implementation progress"
                                                      className="rounded-pill"
                                                    >
                                                      <i className="ri-hammer-line align-middle me-1"></i>
                                                      Update Implementation
                                                    </Button>
                                                  )}

                                                  {/* Fully Implemented Badge - shows when no actions are available */}
                                                  {isFullyImplemented(item) && (
                                                    <Badge color="success" className="p-2">
                                                      Fully Implemented
                                                    </Badge>
                                                  )}

                                                  {/* Show nothing when item is pending delivery or user doesn't have permission */}
                                                  {!canShowImplementButton(item) && !isFullyImplemented(item) && (
                                                    <span className="text-muted">No actions available</span>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>

                                            {index <
                                              purchaseOrder.items.length - 1 && (
                                                <tr>
                                                  <td colSpan={6} className="p-0">
                                                    <hr className="my-1 opacity-25" />
                                                  </td>
                                                </tr>
                                              )}
                                          </React.Fragment>
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <i className="ri-shopping-cart-line display-4 text-muted opacity-50"></i>
                                <h5 className="mt-3 text-muted">
                                  No products in this request
                                </h5>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </TabPane>

                  {/* Documents Tab */}
                  <TabPane tabId="2">
                    <Row>
                      <Col lg={12}>
                        <Card className="border-0 shadow-sm">
                          <CardHeader className="bg-light border-bottom d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0 text-primary">
                              <i className="ri-attachment-line align-middle me-2"></i>{" "}
                              Documents & Attachments
                            </h5>
                            {(IsValidPermissions(["ADD_PURCHASE_REQUEST"]) || IsValidPermissions(["EDIT_PURCHASE_REQUEST"])) &&
                              <Button
                                color="primary"
                                onClick={toggleAttachmentModal}
                                className="rounded-pill"
                              >
                                <i className="ri-add-line align-middle me-1"></i> Add
                                Attachment
                              </Button>
                            }
                          </CardHeader>
                          <CardBody className="p-4">
                            {purchaseOrder?.attachments?.length > 0 ? (
                              <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th scope="col">File Name</th>
                                      <th scope="col">Description</th>
                                      <th scope="col">Uploaded Date</th>
                                      <th scope="col">File Size</th>
                                      <th scope="col" className="text-center">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {purchaseOrder.attachments.map(
                                      (attachment: Attachment) => (
                                        <tr key={attachment.id}>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <i className="ri-file-text-line text-primary fs-5 me-2"></i>
                                              <div>
                                                <div className="fw-semibold">
                                                  <a
                                                    href={attachment.file_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none"
                                                  >
                                                    {attachment.name ||
                                                      getFileName(attachment.file)}
                                                  </a>
                                                </div>
                                                <small className="text-muted">
                                                  {getFileName(attachment.file)}
                                                </small>
                                              </div>
                                            </div>
                                          </td>
                                          <td>
                                            <span className="text-muted">
                                              {attachment.description ||
                                                "No description"}
                                            </span>
                                          </td>
                                          <td>
                                            <small className="text-muted">
                                              {new Date(
                                                attachment.created_at
                                              ).toLocaleDateString()}
                                            </small>
                                          </td>
                                          <td>
                                            <Badge
                                              color="light"
                                              className="text-muted"
                                            >
                                              {attachment.file_size
                                                ? formatFileSize(
                                                  attachment.file_size
                                                )
                                                : "N/A"}
                                            </Badge>
                                          </td>
                                          <td className="text-center">
                                            <div className="hstack gap-2 justify-content-center">
                                              <Button
                                                color="primary"
                                                size="sm"
                                                className="btn-icon"
                                                tag="a"
                                                href={attachment.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="View"
                                              >
                                                <i className="ri-eye-line"></i>
                                              </Button>
                                              <Button
                                                color="success"
                                                size="sm"
                                                className="btn-icon"
                                                tag="a"
                                                href={attachment.file_url}
                                                target="_blank"
                                                download
                                                title="Download"
                                              >
                                                <i className="ri-download-line"></i>
                                              </Button>
                                              {(IsValidPermissions(["EDIT_PURCHASE_REQUEST"]) || IsValidPermissions(["DELETE_PURCHASE_REQUEST"])) && (
                                                <Button
                                                  color="danger"
                                                  size="sm"
                                                  className="btn-icon"
                                                  onClick={() =>
                                                    handleDeleteAttachment(
                                                      attachment.id
                                                    )
                                                  }
                                                  disabled={isDeletingAttachment}
                                                  title="Delete"
                                                >
                                                  {isDeletingAttachment ? (
                                                    <span className="spinner-border spinner-border-sm" />
                                                  ) : (
                                                    <i className="ri-delete-bin-line"></i>
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <i className="ri-attachment-line display-4 text-muted opacity-50"></i>
                                <h5 className="mt-3 text-muted">No Attachments</h5>
                                <p className="text-muted mb-4">
                                  No documents have been attached to this purchase
                                  request yet.
                                </p>
                                {(IsValidPermissions(["ADD_PURCHASE_REQUEST"]) || IsValidPermissions(["EDIT_PURCHASE_REQUEST"])) &&
                                  <Button
                                    color="primary"
                                    onClick={toggleAttachmentModal}
                                    className="rounded-pill"
                                  >
                                    <i className="ri-add-line align-middle me-1"></i>{" "}
                                    Add Attachment
                                  </Button>
                                }
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </TabPane>

                  {/* Document Checklist Tab */}
                  <TabPane tabId="3">
                    <Row>
                      <Col lg={12}>
                        <Card className="border-0 shadow-sm">
                          <CardHeader className="bg-light border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                              <h5 className="card-title mb-0 text-primary">
                                <i className="ri-file-list-3-line align-middle me-2"></i>
                                Document Checklist
                              </h5>
                            </div>
                          </CardHeader>
                          <CardBody className="p-4">
                            {isDocumentChecklistLoading && (
                              <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="sr-only">Loading...</span>
                                </div>
                              </div>
                            )}

                            {documentChecklistError && !isDocumentChecklistLoading && (
                              <div className="alert alert-danger">
                                Unable to load document checklist.
                                {" "}
                                {(() => {
                                  const err: any = documentChecklistError;
                                  const msg =
                                    err?.data?.error ||
                                    err?.data?.detail ||
                                    err?.error ||
                                    err?.statusText;
                                  return msg ? `Details: ${msg}` : "Please try again.";
                                })()}
                              </div>
                            )}

                            {!isDocumentChecklistLoading &&
                              !documentChecklistError && (
                                <>
                                  {documentChecklistData?.documents_by_type &&
                                  documentChecklistData.documents_by_type.length > 0 ? (
                                    <div className="table-responsive">
                                      <Table hover className="align-middle mb-0">
                                        <thead className="table-light">
                                          <tr>
                                            <th scope="col">Document</th>
                                            <th scope="col">Products</th>
                                            <th scope="col" className="text-center">
                                              Uploaded / Required
                                            </th>
                                            <th scope="col" className="text-center">
                                              Status
                                            </th>
                                            <th scope="col" className="text-center">
                                              Uploaded Files
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {documentChecklistData.documents_by_type.map(
                                            (doc: any) => {
                                              const totalRequired =
                                                (doc.product_ids?.length as number) || 0;
                                              const uploadedCount =
                                                (doc.uploaded_count as number) || 0;
                                              const isCompleted =
                                                totalRequired > 0 &&
                                                uploadedCount >= totalRequired;

                                              return (
                                                <tr key={doc.checklist_id}>
                                                  <td>
                                                    <div className="fw-semibold">
                                                      {doc.document_name || "N/A"}
                                                    </div>
                                                    {doc.description && (
                                                      <small className="text-muted d-block">
                                                        {doc.description}
                                                      </small>
                                                    )}
                                                  </td>
                                                  <td>
                                                    {doc.product_names &&
                                                    doc.product_names.length > 0 ? (
                                                      <small className="text-muted">
                                                        {doc.product_names.join(", ")}
                                                      </small>
                                                    ) : (
                                                      <span className="text-muted">
                                                        -
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td className="text-center">
                                                    <span className="fw-semibold">
                                                      {uploadedCount} / {totalRequired}
                                                    </span>
                                                  </td>
                                                  <td className="text-center">
                                                    {isCompleted ? (
                                                      <Badge color="success">
                                                        Completed
                                                      </Badge>
                                                    ) : (
                                                      <Badge color="warning">
                                                        Pending
                                                      </Badge>
                                                    )}
                                                  </td>
                                                  <td className="text-center">
                                                    {doc.uploaded_documents &&
                                                    doc.uploaded_documents.length > 0 ? (
                                                      (() => {
                                                        const file = doc.uploaded_documents?.[0];
                                                        return (
                                                          <div className="d-inline-flex align-items-center gap-2 justify-content-center">
                                                            <Button
                                                              color="primary"
                                                              size="sm"
                                                              className="btn-icon d-inline-flex align-items-center justify-content-center rounded-circle"
                                                              tag="a"
                                                              href={file?.attachment_url}
                                                              target="_blank"
                                                              rel="noopener noreferrer"
                                                              title="Preview"
                                                            >
                                                              <i className="ri-eye-line"></i>
                                                            </Button>
                                                            <Button
                                                              color="success"
                                                              size="sm"
                                                              className="btn-icon d-inline-flex align-items-center justify-content-center rounded-circle"
                                                              tag="a"
                                                              href={file?.attachment_url}
                                                              rel="noopener noreferrer"
                                                              download
                                                              title="Download"
                                                            >
                                                              <i className="ri-download-line"></i>
                                                            </Button>
                                                          </div>
                                                        );
                                                      })()
                                                    ) : (
                                                      <span className="text-muted">
                                                        No uploads
                                                      </span>
                                                    )}
                                                  </td>
                                                </tr>
                                              );
                                            }
                                          )}
                                        </tbody>
                                      </Table>
                                    </div>
                                  ) : (
                                    <div className="text-center py-5">
                                      <i className="ri-file-list-3-line display-4 text-muted opacity-50"></i>
                                      <h5 className="mt-3 text-muted">
                                        No checklist configured
                                      </h5>
                                      <p className="text-muted mb-0">
                                        There are no document checklist items defined
                                        for the products in this purchase request.
                                      </p>
                                    </div>
                                  )}
                                </>
                              )}
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Implement action modal */}
      <Modal isOpen={implementModal} toggle={closeImplementModal} centered>
        <ModalHeader toggle={closeImplementModal}>Update Implementation</ModalHeader>
        <ModalBody>
          <div>
            <p className="mb-3">
              Item: <strong>{selectedItemForAction?.product_name || "N/A"}</strong>
              <br />
              Total Quantity: <strong>{selectedItemForAction?.quantity}</strong>
            </p>
            <Form>
              <FormGroup className="mb-3">
                <Label for="totalImplemented">
                  Total No. of Items Implemented
                </Label>
                <Input
                  type="number"
                  id="totalImplemented"
                  name="total_no_of_items_implemented"
                  value={implementForm.total_no_of_items_implemented}
                  onChange={handleImplementInputChange}
                  placeholder="Enter total implemented"
                  min="0"
                  max={selectedItemForAction?.quantity}
                />
                <div className="form-text">
                  Maximum: {selectedItemForAction?.quantity} items
                </div>
              </FormGroup>
              <FormGroup className="mb-3">
                <Label for="balanceCount">Balance Item Count</Label>
                <Input
                  type="number"
                  id="balanceCount"
                  name="balance_item_count"
                  value={implementForm.balance_item_count}
                  onChange={handleImplementInputChange}
                  placeholder="Enter remaining/balance count"
                  min="0"
                  readOnly
                />
                <div className="form-text">
                  Automatically calculated based on total quantity and implemented count
                </div>
              </FormGroup>
              <FormGroup className="mb-3">
                <Label for="confirmationDoc">
                  Confirmation document / attachment
                  {(isItemDelivered(selectedItemForAction) || isItemStatusDelivered(selectedItemForAction)) && (
                    <span className="text-danger"> *</span>
                  )}
                </Label>
                <Input
                  type="file"
                  id="confirmationDoc"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setImplementConfirmationFile(file);
                  }}
                />
                <div className="form-text">
                  {(isItemDelivered(selectedItemForAction) || isItemStatusDelivered(selectedItemForAction))
                    ? "Required when status is Delivered. Upload a confirmation document or attachment."
                    : "Optional. Upload a confirmation document or attachment."}
                </div>
                {implementConfirmationFile && (
                  <div className="form-text text-success mt-1">
                    Selected: {implementConfirmationFile.name}
                  </div>
                )}
              </FormGroup>
            </Form>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeImplementModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleImplementSubmit}
            disabled={isUpdatingItem}
          >
            {isUpdatingItem ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : null}
            Update Implementation
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={confirmImplementModal}
        toggle={() => setConfirmImplementModal(!confirmImplementModal)}
        centered
      >
        <ModalHeader toggle={() => setConfirmImplementModal(!confirmImplementModal)}>
          Confirm Implementation Update
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            You are about to submit implementation details for:
            <strong> {selectedItemForAction?.product_name || "this item"}</strong>.
          </p>
          <p className="mb-0 text-danger">
            This can be updated only once. Do you want to continue?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setConfirmImplementModal(false)}
            disabled={isUpdatingItem}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleImplementConfirmSubmit}
            disabled={isUpdatingItem}
          >
            {isUpdatingItem ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : null}
            Confirm & Submit
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reject Purchase Request modal */}
      <Modal isOpen={rejectModal} toggle={toggleRejectModal} centered>
        <ModalHeader toggle={toggleRejectModal}>
          Reject Purchase Request
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="rejectReason">
              Reason for rejection <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              id="rejectReason"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejecting this purchase request..."
              disabled={isUpdatingStatus}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleRejectModal} disabled={isUpdatingStatus}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleRejectConfirm}
            disabled={isUpdatingStatus || !rejectReason.trim()}
          >
            {isUpdatingStatus ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : null}
            Confirm Rejection
          </Button>
        </ModalFooter>
      </Modal>

      {/* Attachment modal */}
      <Modal isOpen={attachmentModal} toggle={toggleAttachmentModal} size="lg">
        <ModalHeader toggle={toggleAttachmentModal}>
          Add New Attachment
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup className="mb-3">
              <Label for="attachmentFile">
                File <span className="text-danger">*</span>
              </Label>
              <Input
                type="file"
                id="attachmentFile"
                onChange={handleFileChange}
                accept="*/*"
              />
              <div className="form-text">
                Select a file to upload. All file types are supported.
              </div>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label for="attachmentName">Name</Label>
              <Input
                type="text"
                id="attachmentName"
                name="name"
                placeholder="Enter attachment name"
                value={newAttachment.name}
                onChange={handleAttachmentInputChange}
              />
              <div className="form-text">
                Optional: Give a descriptive name for this attachment.
              </div>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label for="attachmentDescription">Description</Label>
              <Input
                type="textarea"
                id="attachmentDescription"
                name="description"
                placeholder="Enter attachment description"
                value={newAttachment.description}
                onChange={handleAttachmentInputChange}
                rows={3}
              />
              <div className="form-text">
                Optional: Add a description for this attachment.
              </div>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleCreateAttachment}
            disabled={!newAttachment.file || isCreatingAttachment}
          >
            {isCreatingAttachment ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Uploading...
              </>
            ) : (
              <>
                <i className="ri-upload-line align-middle me-1" />
                Upload Attachment
              </>
            )}
          </Button>
          <Button color="secondary" onClick={toggleAttachmentModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Cancel PR Modal */}
      <CancelReasonModal
        show={cancelPRModalOpen}
        title="Cancel Purchase Request"
        description="This will cancel the purchase request. It cannot have any active purchase orders."
        isSubmitting={isCancellingPR}
        errorText={cancelPRError}
        errorItems={cancelPRErrorItems}
        onConfirm={handleCancelPR}
        onClose={() => setCancelPRModalOpen(false)}
      />
    </div>
  );
};

export default PurchaseOrderDetail;